import { Suspense, lazy, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  useRoutes,
  createBrowserRouter,
  Outlet,
  RouterProvider,
  Link as RouterLink,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';
import _ from 'lodash';
import {PersistGate} from 'redux-persist/lib/integration/react';

// layouts
import AdminLayout from '../layouts/admin-layout';
import LauanLayout from '../layouts/lauan-layout';
import SessionLayout from '../layouts/session-layout';

import SimpleLayout from '../layouts/base-layout';
import IcivetLayout from '../layouts/icivet-layout';
// guard
import { AdminRequireAuth } from '../components/guard';
import { ScrollToTop } from '../components/scroll-to-top';
import { SplashScreen, PreloaderScreen } from '../components/loading-screen';
import { PATH_CONFIG, PATH_HONHAI } from './paths';
import { AuthProvider } from '../contexts/AuthContext';
import { GLOBAL_CATALOGS } from '../store/types';
import { PageHandleBreadcrumbs, PageLauanBreadcrumbs } from '../components/pages';
import { persistor } from '../store';


const Splash = (Component) => (props) =>
  (
    <Suspense fallback={<SplashScreen />}>
       <Component {...props} />
    </Suspense>
  );

// auth
const IcivetAuthorizePage = Splash(lazy(() => import('../pages/sessions/IcivetAuthorize')) );

// sessions
const LoginPage = Splash(lazy(() => import('../pages/sessions/LoginPage')) );
const RegisterPage = Splash(lazy(() => import('../pages/sessions/Register')) );
const ForgetPasswordPage = Splash(lazy(() => import('../pages/sessions/ForgetPasswordPage')) );


const DashboardPage = Splash(lazy(() => import('../pages/views/dashboard')) );
const ApplicationsPage = Splash(lazy(() => import('../pages/views/applications/Default')) );

const ProfilePage = Splash(lazy(() => import('../pages/views/user/Default')) );
const GeneralInformationPage = Splash(lazy(() => import('../pages/views/user/Statistics/Profile')) );
const ProfileChangePasswordPage = Splash(lazy(() => import('../pages/views/user/Statistics/ChangePassword')) );
const ProfileReplyPage = Splash(lazy(() => import('../pages/views/user/Statistics/ReplyList')) );
const ProfileRepairPage = Splash(lazy(() => import('../pages/views/user/Statistics/RepairList')) );
const ProfileDetailsPage = Splash(lazy(() => import('../pages/views/user/Details')) );


const SearchResultPage = Splash(lazy(() => import('../pages/views/search')) );


const CateringServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/CateringService')) );
const PostalServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/PostalService')) );
const RepairServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/RepairService')) );
const TrafficServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/TrafficService')) );
const FoodServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/FoodService')) );
const DormitoryServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/DormitoryService')) );
const EnvironmentServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/EnvironmentService')) );
const GreeningServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/GreeningService')) );
const WorshipServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/WorshipService')) );
const CleanServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/CleanService')) );

// 共享辦公室
const OfficeServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/OfficeService')) );
const OfficeCartPage = Splash(lazy(() => import('../pages/views/service/Details/Carts/OfficeAppCart')) );
// const OfficeCartPage = Splash(lazy(() => import('../pages/views/service/Details/Carts/OfficeAppyCart')) );
const OfficeApplyDetailsPage = Splash(lazy(() => import('../pages/views/service/Details/OfficeApplyDetails')) );

const OfficeChangeChildDetailsPage = Splash(lazy(() => import('../pages/views/service/Details/OfficeChangeChildDetails')) );

const OfficeWaitDetailsPage = Splash(lazy(() => import('../pages/views/service/Details/OfficeWaitDetails')) );


/* ************************************************************ */
const CoworkingServicePage = Splash(lazy(() => import('../pages/views/service/Statistics/CoworkingService')) );
const CoworkingCartPage = Splash(lazy(() => import('../pages/views/service/Receipt/Carts/OfficeAppCart')) );

const CoworkingApplyDetailsPage = Splash(lazy(() => import('../pages/views/service/Receipt/OfficeApply/OfficeApplyDetails')) );
const CoworkingChangeChildDetailsPage = Splash(lazy(() => import('../pages/views/service/Receipt/OfficeChange/OfficeChangeChildDetails')) );
const CoworkingWaitDetailsPage = Splash(lazy(() => import('../pages/views/service/Receipt/OfficeWait/OfficeWaitDetails')) ); 
/* ************************************************************ */

const AboutPage = Splash(lazy(() => import('../pages/views/about/Default')) );
const AboutBusinessIntroducePage = Splash(lazy(() => import('../pages/views/about/Statistics/BusinessIntroduce')) );
const AboutFactoryContactPage = Splash(lazy(() => import('../pages/views/about/Statistics/FactoryContact')) );
const AboutStationaryPositionPage = Splash(lazy(() => import('../pages/views/about/Statistics/StationaryPosition')) );


const BulletinsPage = Splash(lazy(() => import('../pages/views/bulletins/Default')) );
const BulletinsLatestNewsPage = Splash(lazy(() => import('../pages/views/bulletins/Statistics/LatestNews')) );
const BulletinsAnnouncementsPage = Splash(lazy(() => import('../pages/views/bulletins/Statistics/Announcements')) );


const DocumentsPage = Splash(lazy(() => import('../pages/views/documents/Default')) );
const DocumentsGeneralOrderPage = Splash(lazy(() => import('../pages/views/documents/Statistics/GeneralOrderList')) );
const DocumentsMaterialDownloadPage = Splash(lazy(() => import('../pages/views/documents/Statistics/MaterialDownloadList')) );


const FaqsPage = Splash(lazy(() => import('../pages/views/faqs/Default')) );
const FaqsGeneralAffairsListPage = Splash(lazy(() => import('../pages/views/faqs/Statistics/GeneralAffairsList')) );
const FaqsMechanicalElectricalListPage = Splash(lazy(() => import('../pages/views/faqs/Statistics/MechanicalElectricalList')) );
const FaqsSafetyHealthListPage = Splash(lazy(() => import('../pages/views/faqs/Statistics/SafetyHealthList')) );


const ContactUsPage = Splash(lazy(() => import('../pages/views/contact/Default')) );
const ContactUsFormPage = Splash(lazy(() => import('../pages/views/contact/Statistics/ContactUsForm')) );

// 簽核中心
const SignOffPage = Splash(lazy(() => import('../pages/views/signoff/Default')) );
const SignOffActionDetailsPage = Splash(lazy(() => import('../pages/views/signoff/Details/SignOffActionDetails')) );
const SignOffViewDetailsPage = Splash(lazy(() => import('../pages/views/signoff/Details/SignOffViewDetails')) );

const LaborPage = Splash(lazy(() => import('../pages/views/labor/Default')) );
const LauanDashboardPage = Splash(lazy(() => import('../pages/lauan/home/Default')) );
const LauanPolicyPage = Splash(lazy(() => import('../pages/lauan/home/Statistics/Policy')) );
const LauanNewsPage = Splash(lazy(() => import('../pages/lauan/home/Statistics/News')) );
const LauanReportPage = Splash(lazy(() => import('../pages/lauan/home/Statistics/Report')) );
const LauanFaqsPage = Splash(lazy(() => import('../pages/lauan/home/Statistics/Faqs')) );
const LauanSiteMapPage = Splash(lazy(() => import('../pages/lauan/map/SiteMap')) );
const LauanContentPage = Splash(lazy(() => import('../pages/lauan/content')) );


const ContentDetailsPage = Splash(lazy(() => import('../pages/views/content/ContentDetails')) );
const ContentPage = Splash(lazy(() => import('../pages/views/content')) );


const IconsPage = Splash(lazy(() => import('../pages/views/utilities/Icons')) );
const LawAndTrademarkPage = Splash(lazy(() => import('../pages/views/utilities/LawAndTrademark')) );
const SpecificationPage = Splash(lazy(() => import('../pages/views/utilities/Specification')) );
const PrivacyAndPolicyPage = Splash(lazy(() => import('../pages/views/utilities/PrivacyAndPolicy')) );
const CookiePolicyPage = Splash(lazy(() => import('../pages/views/utilities/CookiePolicy')) );
const SiteMapPage = Splash(lazy(() => import('../pages/views/utilities/SiteMap')) );
const SiteLinksPage = Splash(lazy(() => import('../pages/views/utilities/SiteLinks')) );

const Page404 = Splash(lazy(() => import('../pages/Page404')) );
const PageUnderConstruction = Splash(lazy(() => import('../pages/PageUnderConstruction')) );
const LoadingAuthorize = Splash(lazy(() => import('../pages/sessions/LoadingAuthorize')) );

// Utility function to generate breadcrumbs
const generateBreadcrumbs = (global, codes, extra = []) => {
  return [..._.filter(global?.list || [], item => codes.includes(item?.contentCode)), ...extra];
};

// Constants for labels
const LABELS = {
  SITE_MAP: '網站地圖',
  LAW_TRADEMARK: '法律與商標',
  SPECIFICATION: '規範',
  PRIVACY_POLICY: '隱私權與政策',
  COOKIE_POLICY: 'Cookie 政策',
  DETAILS: '詳情',
  RECEIPT_DETAILS: '單據詳情',
  OFFICE_APPLY_RECEIPT_DETAILS: '共享辦公申請單',
  OFFICE_WAIT_RECEIPT_DETAILS: '共享辦公候補單',
  OFFICE_CHANGE_RECEIPT_DETAILS: '共享辦公變更單',
  PERSONAL_CENTER: '個人中心',
  MY_RESPONSE: '我的回應',
  MY_REPAIR: '我的報修'
};

// ================================|| SERVICE ||================================ //
const SERVICE = ({ state, dispatch }) => ([/* **************2.0******** */
  {
    path: 'service/catering_service/:catalogs?',
    element: <CateringServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { catalogs } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Catering.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => {
        return <PageHandleBreadcrumbs list={[...data]} />
      }
    },
  },

  {
    path: 'service/postal_service/:catalogs?',
    element: <PostalServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Postal.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/repair_service',
    element: <RepairServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Repair.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/traffic_service/:catalogs?',
    element: <TrafficServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Traffic.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/food_service/:catalogs?',
    element: <FoodServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Food.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/accommodation_service/:catalogs?',
    element: <DormitoryServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Dormitory.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/energy_environmental/:catalogs?',
    element: <EnvironmentServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Environment.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/greening_plant_service/:catalogs?',
    element: <GreeningServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Greening.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/sacrificial_service/:catalogs?',
    element: <WorshipServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Worship.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/clean_service/:catalogs?',
    element: <CleanServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Clean.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/coworking_service/:catalogs?',
    element: <OfficeServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/coworking_cart/:factoryName?/:factoryId?/:tenancyTypeName?/:rentType?',
    element: <OfficeCartPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  // 申請單-創建 & 編輯 & 變更 & 查看
  {
    path: 'service/coworking_receipt',
    element: <OfficeApplyDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service], [{ label: LABELS.OFFICE_APPLY_RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },
  

  // 變更單-編輯&&查看
  {
    path: 'service/coworking_change_child_details',
    element: <OfficeChangeChildDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service], [{ label: LABELS.OFFICE_CHANGE_RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  // 候補單-編輯&&查看
  {
    path: 'service/coworking_wait_receipt',
    element: <OfficeWaitDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service], [{ label: LABELS.OFFICE_WAIT_RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },
  /* **************2.0******** */


  {
    path: 'service/office_service/:catalogs?',
    element: <CoworkingServicePage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  {
    path: 'service/office_cart/:factoryName?/:factoryId?/:tenancyTypeName?/:rentType?',
    element: <CoworkingCartPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  // 申請單-創建 & 編輯 & 變更 & 查看
  {
    path: 'service/office_receipt',
    element: <CoworkingApplyDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service], [{ label: LABELS.OFFICE_APPLY_RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },
  

  // 變更單-編輯&&查看
  {
    path: 'service/office_change_child_details',
    element: <CoworkingChangeChildDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service], [{ label: LABELS.OFFICE_CHANGE_RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },

  // 候補單-編輯&&查看
  {
    path: 'service/office_wait_receipt',
    element: <CoworkingWaitDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      const { page, category, content } = arg?.params || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Service.Root, GLOBAL_CATALOGS.Coworking.Service], [{ label: LABELS.OFFICE_WAIT_RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={[...data]} />)
    },
  },
]);


// ==============================|| ABOUTS ||=================================== //
const ABOUTS = ({ state, dispatch }) => ([
  {
    path: 'about',
    element: <AboutPage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.About.Root]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  {
    path: 'about/business-introduce',
    element: <AboutBusinessIntroducePage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.About.Root, GLOBAL_CATALOGS.About.Introduce.List]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  {
    path: 'about/factory-contact',
    element: <AboutFactoryContactPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.About.Root, GLOBAL_CATALOGS.About.Casement.List]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  {
    path: 'about/stationary-position',
    element: <AboutStationaryPositionPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.About.Root, GLOBAL_CATALOGS.About.Stationary.List]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  
]);

// ==============================|| BULLETINS ||============================= //
const BULLETINS = ({ state, dispatch }) => ([
  {
    path: 'bulletins/news',
    element: <BulletinsLatestNewsPage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Bulletins.Root]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
  {
    path: 'bulletins/announcements',
    element: <BulletinsAnnouncementsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Bulletins.Root, GLOBAL_CATALOGS.Bulletins.Announcement.List]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
]);

// ==============================|| FAQS ||===================================== //
const FAQS = ({ state, dispatch }) => ([
  {
    path: 'faqs',
    element: <FaqsPage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Faqs.Root]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  {
    path: 'faqs/:catalogs',
    element: <FaqsPage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Faqs.Root]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  {
    path: 'faqs-analytics',
    element: <FaqsPage />,
    loader: (args) => {
      return true;
    },
    children: [
      {
        element: <Navigate to={`${PATH_HONHAI.root}/faqs-analytics/affairs`} />,
        index: true
      },
      {
        path: 'affairs',
        element: <FaqsGeneralAffairsListPage /> ,
        loader: (arg) => {
          const { global } = state?.account || {};
          return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Faqs.Root, GLOBAL_CATALOGS.Faqs.Affairs.List]);
        },
        handle: {
          // scrollMode: 'pathname',
          crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
        },
      },
      {
        path: 'mechanical',
        element: <FaqsMechanicalElectricalListPage />,
        loader: (arg) => {
          const { global } = state?.account || {};
          return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Faqs.Root, GLOBAL_CATALOGS.Faqs.Electrical.List]);
        },
        handle: {
          // scrollMode: 'pathname',
          crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
        },
      },
      {
        path: 'safety',
        element: <FaqsSafetyHealthListPage />,
        loader: (arg) => {
          const { global } = state?.account || {};
          return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Faqs.Root, GLOBAL_CATALOGS.Faqs.Safety.List]);
        },
        handle: {
          // scrollMode: 'pathname',
          crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
        },
      },
      {
        path: 'detail',
        element: <FaqsSafetyHealthListPage />,
        // handle: { scrollMode: "pathname" },
      },
    ]
  },
]);

// ==============================|| DOCUMENTS ||================================ //
const DOCUMENTS = ({ state, dispatch }) => ([
  {
    path: 'documents',
    element: <DocumentsPage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Documents.Root]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  {
    path: 'documents/:catalogs',
    element: <DocumentsPage /> ,
    loader: (arg) => {
      const { params } = arg || {};
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Documents.Root]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
]);

// ==============================|| CONTACT ||================================= //
const CONTACT = ({ state, dispatch }) => ([
  {
    path: 'contact',
    element: <ContactUsPage />,
  },

  {
    path: 'contact/us',
    element: <ContactUsFormPage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Contact.Root, GLOBAL_CATALOGS.Contact.Us.List]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
]);

// ==============================|| SIGNOFF ||================================= //
const SIGNOFF = ({ state, dispatch }) => ([
  {
    path: 'sign-off/:catalogs?',
    element: <SignOffPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.SignOff.Root]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
  {
    path: 'sign-off-action/details',
    element: <SignOffActionDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.SignOff.Root], [{ label: LABELS.RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },

  {
    path: 'sign-off-view/details',
    element: <SignOffViewDetailsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.SignOff.Root], [{ label: LABELS.RECEIPT_DETAILS }]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
]);

// ==============================|| SEARCH ||================================== //
const SEARCH = ({ state, dispatch }) => ([
  {
    path: 'search-result/:key',
    element: <SearchResultPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.FullTextSearch]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
]);


// ==============================|| APPLICATIONS ||============================= //
const APPLICATIONS = ({ state, dispatch }) => ([
  {
    path: 'applications',
    element: <ApplicationsPage />,
    loader: (arg) => {
      const { global } = state?.account || {};
      return generateBreadcrumbs(global, [GLOBAL_CATALOGS.ServiceApplication]);
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  }
]);

// ==============================|| PROFILE ||============================= //
const PROFILE = ({ state, dispatch }) => ([
  {
    path: 'user',
    element: <Navigate to={`${PATH_HONHAI.user.root}/user_basic_information`} />
  },
  {
    path: 'user/user_basic_information',
    element: <GeneralInformationPage /> ,
    loader: (arg) => {
      const { global } = state?.account || {};
      return [{ id: Math.random(), label: '個人中心' }, { id: Math.random(), label: '個人基本資料', props: { path: '/honhai/user/user_basic_information' } }];
    },
    handle: {
      // scrollMode: 'pathname',
      crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
    },
  },
  {
    path: 'user',
    element: <ProfilePage />,
    loader: (arg) => {
      return true;
    },
    handle: {
      // scrollMode: 'pathname',
    },
    children: [
      {
        path: 'user_information',
        element: <GeneralInformationPage /> ,
        loader: (arg) => {
          const { global } = state?.account || {};
          return [{ id: Math.random(), label: '個人中心' }, { id: Math.random(), label: '個人基本資料', props: { path: '/honhai/user/user_basic_information' } }];
        },
        handle: {
          // scrollMode: 'pathname',
          crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
        },
      },
      {
        path: 'user_change_password',
        element: <ProfileChangePasswordPage />,
        loader: (arg) => {
          const { global } = state?.account || {};
          return [{ id: Math.random(), label: '個人中心' }, { id: Math.random(), label: '修改密碼', props: { path: '/honhai/user/user_change_password' } }];
        },
        handle: {
          // scrollMode: 'pathname',
          crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
        },
      },
      {
        path: 'user_response',
        element: <ProfileReplyPage /> ,
        loader: (arg) => {
          const { global } = state?.account || {};
          return [{ id: Math.random(), label: '個人中心' }, { id: Math.random(), label: '我的回應', props: { path: '/honhai/user/user_response' } }];
        },
        handle: {
          // scrollMode: 'pathname',
          crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
        },
      },
      {
        path: 'user_repair',
        element: <ProfileRepairPage />,
        loader: (arg) => {
          const { global } = state?.account || {};
          return [{ id: Math.random(), label: '個人中心' }, { id: Math.random(), label: '我的報修', props: { path: '/honhai/user/user_repair' } }];
        },
        handle: {
          // scrollMode: 'pathname',
          crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
        },
      },
    ],
  },
  
]);

 
// Routes configuration
const routes = ({ state, dispatch, getKey }) => createBrowserRouter([
  {
    element: (
      <PersistGate loading={null} persistor={persistor}>
        <AuthProvider>
          <Outlet />
          <ScrollToTop />
          {/* <ScrollRestoration getKey={getKey} /> */}
        </AuthProvider>
      </PersistGate>
    ),
    children: [
      {
        path: PATH_HONHAI.root,
        element: (
          <AdminRequireAuth>
            <AdminLayout />
          </AdminRequireAuth>
        ),
        children: [
          { element: <Navigate to={`${PATH_HONHAI.root}/app`} />, index: true },
          {
            path: 'app',
            element: <DashboardPage />,
            handle: { scrollMode: "pathname" },
            loader: async (args) => true,
          },
          ...SERVICE({ state, dispatch }),
          ...ABOUTS({ state, dispatch }),
          ...BULLETINS({ state, dispatch }),
          ...FAQS({ state, dispatch }),
          ...DOCUMENTS({ state, dispatch }),
          ...CONTACT({ state, dispatch }),
          ...SEARCH({ state, dispatch }),
          ...APPLICATIONS({ state, dispatch }),
          ...PROFILE({ state, dispatch }),
          ...SIGNOFF({ state, dispatch }),

          // Content Details
          {
            path: 'details/:category?/:content?/:catalogId/:contentType/:contentId/:leve1ContentCode?',
            element: <ContentPage />,
            loader: ({ request, params }) => {
              const { global } = state?.account || {};
              const { leve1ContentCode, category, content } = params || {};
              return generateBreadcrumbs(global, [leve1ContentCode, category, content], [{ label: LABELS.DETAILS }]);
            },
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },

          // Test Content Details
          {
            path: 'content/details/:level1ContentCode?/:level2ContentCode?',
            element: <ContentDetailsPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              const { level1ContentCode, level2ContentCode } = arg?.params || {};
              return generateBreadcrumbs(global, [level1ContentCode, level2ContentCode], [{ label: LABELS.DETAILS }]);
            },
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },

          // Profile Details
          {
            path: '/honhai/:messageType/details/:id',
            element: <ProfileDetailsPage />,
            loader: (arg) => {
              const { params } = arg || {};
              const { messageType } = params || {};
              const bread = {
                user_response: { id: Math.random(), label: LABELS.MY_RESPONSE, props: { path: '/honhai/user/user_response' } },
                user_repair: { id: Math.random(), label: LABELS.MY_REPAIR, props: { path: '/honhai/user/user_repair' } }
              };
              return [{ id: Math.random(), label: LABELS.PERSONAL_CENTER }, messageType && bread[messageType], { label: LABELS.DETAILS }];
            },
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },

          // Static Pages
          {
            path: 'site-map',
            element: <SiteMapPage />,
            loader: (arg) => [{ label: LABELS.SITE_MAP }],
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },
          {
            path: 'site-links',
            element: <SiteLinksPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Link]);
            },
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },
          {
            path: 'law-trademark',
            element: <LawAndTrademarkPage />,
            loader: (arg) => [{ label: LABELS.LAW_TRADEMARK }],
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },
          {
            path: 'specification',
            element: <SpecificationPage />,
            loader: (arg) => [{ label: LABELS.SPECIFICATION }],
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },
          {
            path: 'privacy-policy',
            element: <PrivacyAndPolicyPage />,
            loader: (arg) => [{ label: LABELS.PRIVACY_POLICY }],
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },
          {
            path: 'cookie-policy',
            element: <CookiePolicyPage />,
            loader: (arg) => [{ label: LABELS.COOKIE_POLICY }],
            handle: {
              crumb: (data, params) => (<PageHandleBreadcrumbs list={data} />)
            },
          },
        ],
      },
      // Lauan Routes
      {
        path: PATH_HONHAI.root,
        element: (
          <AdminRequireAuth>
            <LauanLayout />
          </AdminRequireAuth>
        ),
        children: [
          {
            path: 'lauan/app',
            element: <LauanDashboardPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              const { category, content, id } = arg?.params || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Labor.Service, category, content]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
          {
            path: 'lauan/occupational_safety_policy',
            element: <LauanPolicyPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Labor.Service, GLOBAL_CATALOGS.Labor.Policy.Report]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
          {
            path: 'lauan/occupational_safety_news',
            element: <LauanNewsPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Labor.Service, GLOBAL_CATALOGS.Labor.News.List]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
          {
            path: 'lauan/report/:catalogs',
            element: <LauanReportPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Labor.Service, GLOBAL_CATALOGS.Labor.Test.Report]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
          {
            path: 'lauan/detection_report',
            element: <LauanReportPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Labor.Service, GLOBAL_CATALOGS.Labor.Test.Report]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
          {
            path: 'lauan/safety_health_faqs',
            element: <LauanFaqsPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              const { category, content, id } = arg?.params || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Labor.Service, GLOBAL_CATALOGS.Labor.FAQS.List]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
          {
            path: 'lauan/site-map',
            element: <LauanSiteMapPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              const { category, content, id } = arg?.params || {};
              return generateBreadcrumbs(global, [GLOBAL_CATALOGS.Labor.Service], [{ label: LABELS.SITE_MAP }]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
          {
            path: 'lauan/details/:category?/:content?/:catalogId/:contentType/:contentId',
            element: <LauanContentPage />,
            loader: (arg) => {
              const { global } = state?.account || {};
              const { category, content } = arg?.params || {};
              return generateBreadcrumbs(global, [category, content], [{ label: LABELS.DETAILS }]);
            },
            handle: {
              crumb: (data, params) => (<PageLauanBreadcrumbs list={data} />)
            },
          },
        ],
      },
      // Session Routes
      {
        path: '/session',
        element: <SessionLayout />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'register', element: <RegisterPage /> },
          { path: 'forget-password', element: <ForgetPasswordPage /> },
        ]
      },
      // Auth Routes
      {
        path: '/auth',
        element: <IcivetLayout />,
        children: [
          { path: 'icivet', element: <IcivetAuthorizePage /> },
        ]
      },
      { path: 'sso-authorize', element: <LoadingAuthorize /> },
    ]
  },
  
  { path: 'icons', element: <IconsPage /> },
  {
    element: (
      <AuthProvider>
        <SimpleLayout disabledLink />
      </AuthProvider>
    ),
    children: [
      { path: 'under-construction', element: <PageUnderConstruction /> },
      { path: '/under-construction', element: <PageUnderConstruction /> },
    ],
  },
  {
    element: <SimpleLayout />,
    children: [
      { element: <Navigate to="/honhai/app" />, index: true },
      { path: '404', element: <Page404 /> },
      { path: '*', element: <Navigate to="/404" /> },
    ],
  },
], { basename: PATH_CONFIG.basename });


// ==============================|| ROUTER ||================================ //
export default function Router() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const state = useSelector(state => state);

  const getKey = useCallback(
    (location, matches) => {
      const match = matches.find((m) => m.handle?.scrollMode);
      if (match?.handle?.scrollMode === "pathname") {
        return location.pathname;
      }
      return location.key;
    },
    []
  );

  // const getKey = useCallback((location, matches) => {
  //   const paths = ["/honhai/service"];
  //   return paths.includes(location.pathname)
  //     ? // home and notifications restore by pathname
  //     location.pathname
  //     : // everything else by location like the browser
  //     location.key;
  // }, [])

  return <RouterProvider router={routes({ state, dispatch })} fallbackElement={<PreloaderScreen />} />;
};